import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  batchesApi,
  templatesApi,
  signaturesApi,
  jobsApi,
  classMapApi,
  CERT_TYPES,
} from "../../../service/certificate"
import { getAllClasses } from "../../../service/classes"

const typeLabel = (v: string) => CERT_TYPES.find(t => t.value === v)?.label || v

// ── Generate-from-batch modal ──────────────────────────────────────
const GenerateModal = ({ batch, onClose, onDone }: { batch: any; onClose: () => void; onDone: () => void }) => {
  const [templates, setTemplates] = useState<any[]>([])
  const [templateId, setTemplateId] = useState("")
  const [signatures, setSignatures] = useState<any[]>([])
  const [sigIds, setSigIds] = useState<string[]>([])
  const [pageMode, setPageMode] = useState("native")
  const [useClassTemplates, setUseClassTemplates] = useState(false)
  const [classMap, setClassMap] = useState<Record<string, { id: string; name: string }>>({})
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState("")
  const [resultUrl, setResultUrl] = useState("")

  useEffect(() => {
    templatesApi.list({ certificateType: batch.type }).then(r => setTemplates(r.data || [])).catch(() => {})
    signaturesApi.list().then(r => setSignatures(r.data || [])).catch(() => {})
  }, [batch.type])

  const [classes, setClasses] = useState<any[]>([])
  useEffect(() => {
    if (useClassTemplates) {
      classMapApi(batch.type).then(r => setClassMap(r.data || {})).catch(() => setClassMap({}))
      if (classes.length === 0)
        getAllClasses({ limit: 1000, page: 1 } as any)
          .then((r: any) => setClasses(r?.data || r?.classes || (Array.isArray(r) ? r : [])))
          .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useClassTemplates, batch.type])

  const classNameById = useMemo(
    () => Object.fromEntries((classes || []).map((c: any) => [String(c._id), c.name])),
    [classes]
  )
  // RoutingPreview (RMS): group the class→template map into template → class chips.
  const routingGroups = useMemo(() => {
    const m: Record<string, { name: string; classes: string[] }> = {}
    Object.entries(classMap).forEach(([cid, t]: any) => {
      if (!m[t.id]) m[t.id] = { name: t.name, classes: [] }
      m[t.id].classes.push(classNameById[String(cid)] || String(cid))
    })
    return Object.values(m)
  }, [classMap, classNameById])

  const toggleSig = (id: string) =>
    setSigIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))

  const generate = async () => {
    if (!templateId) return toast.error("Pick a template.")
    setBusy(true)
    setResultUrl("")
    setProgress("Starting...")
    try {
      const start = await batchesApi.generate(batch._id, {
        templateId,
        signatureIds: sigIds,
        pageMode,
        useClassTemplates,
      })
      const jobId = start.jobId
      if (!jobId) {
        toast.error("No job id returned")
        setBusy(false)
        return
      }
      // Poll the job until it finishes.
      const poll = async () => {
        try {
          const j = await jobsApi.get(jobId)
          const job = j.data
          if (job.status === "completed") {
            const result = job.result
            if (result?.pdfUrl) {
              setResultUrl(result.pdfUrl)
              toast.success(`Generated ${result.studentCount} certificate(s)`)
              onDone()
            } else {
              toast.error("Generation returned no PDF")
            }
            setBusy(false)
            setProgress("")
          } else if (job.status === "failed") {
            toast.error(job.error || "Generation failed")
            setBusy(false)
            setProgress("")
          } else {
            setProgress(job.total ? `Rendering ${job.progress}/${job.total}...` : "Rendering...")
            setTimeout(poll, 2000)
          }
        } catch {
          setTimeout(poll, 2500)
        }
      }
      poll()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Generation failed")
      setBusy(false)
      setProgress("")
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Generate — {batch.fileName}{" "}
                {batch.refNumber && <span className="badge bg-secondary ms-1">{batch.refNumber}</span>}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Template *</label>
                <select className="form-select" value={templateId} onChange={e => setTemplateId(e.target.value)}>
                  <option value="">Select a {typeLabel(batch.type)} template...</option>
                  {templates.map(t => (
                    <option key={t._id} value={t._id}>{t.name}</option>
                  ))}
                </select>
                {templates.length === 0 && (
                  <small className="text-danger">No templates for this type — create one first.</small>
                )}
              </div>

              {signatures.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Signatures</label>
                  <div className="d-flex flex-wrap gap-3">
                    {signatures.map(sig => (
                      <div className="form-check" key={sig._id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={sigIds.includes(sig._id)}
                          onChange={() => toggleSig(sig._id)}
                          id={`g-sig-${sig._id}`}
                        />
                        <label className="form-check-label" htmlFor={`g-sig-${sig._id}`}>
                          {sig.signerName} ({sig.signerTitle})
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-2">
                <label className="form-label">Page size</label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="pageMode"
                      id="pm-native"
                      checked={pageMode === "native"}
                      onChange={() => setPageMode("native")}
                    />
                    <label className="form-check-label" htmlFor="pm-native">12 × 10 in <span className="text-muted">(canvas)</span></label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="pageMode"
                      id="pm-a4"
                      checked={pageMode === "a4"}
                      onChange={() => setPageMode("a4")}
                    />
                    <label className="form-check-label" htmlFor="pm-a4">A4 landscape <span className="text-muted">(crop to A4 boundary)</span></label>
                  </div>
                </div>
              </div>

              {/* Auto-routing by class — each class uses its own assigned template */}
              <div className="mt-3 border rounded p-2 bg-light">
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="useClassTpl"
                    checked={useClassTemplates}
                    onChange={e => setUseClassTemplates(e.target.checked)}
                  />
                  <label className="form-check-label small fw-semibold" htmlFor="useClassTpl">
                    Auto-route by class (use per-class template assignments)
                  </label>
                </div>
                {useClassTemplates && (
                  <div className="mt-2">
                    {routingGroups.length === 0 ? (
                      <span className="text-muted small">No class assignments found — all students will use the template above as fallback.</span>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {routingGroups.map((g, i) => (
                          <div key={i}>
                            <div className="fw-semibold small mb-1">{g.name}</div>
                            <div className="d-flex flex-wrap gap-1">
                              {g.classes.map((cn, j) => (
                                <span key={j} className="badge bg-primary-transparent text-primary">{cn}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-muted small mb-0 mt-2">Each template only renders students whose class is mapped to it; students in unmapped classes use the template selected above.</p>
                  </div>
                )}
              </div>

              {resultUrl && (
                <div className="alert alert-success d-flex align-items-center justify-content-between mt-2 mb-0">
                  <span><i className="ti ti-circle-check me-2" />Done.</span>
                  <a href={resultUrl} target="_blank" rel="noreferrer" className="btn btn-success btn-sm">
                    <i className="ti ti-download me-1" />Download PDF
                  </a>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-light" onClick={onClose} disabled={busy}>Close</button>
              <button className="btn btn-primary" onClick={generate} disabled={busy || !templateId}>
                {busy ? (
                  <><span className="spinner-border spinner-border-sm me-2" />{progress || "Generating..."}</>
                ) : (
                  <><i className="ti ti-certificate me-1" />Generate</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── View-batch modal ───────────────────────────────────────────────
const ViewModal = ({ batch, onClose }: { batch: any; onClose: () => void }) => {
  const rows: any[] = Array.isArray(batch.studentsData) ? batch.studentsData : []
  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{batch.fileName} — {rows.length} students</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body table-responsive" style={{ maxHeight: "60vh", overflow: "auto" }}>
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>GR</th>
                    <th>Name</th>
                    <th>Class</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const { student_id, full_name, gr_number, class_name, section_name, ...rest } = r
                    return (
                      <tr key={i}>
                        <td>{gr_number || "—"}</td>
                        <td>{full_name || "—"}</td>
                        <td>{class_name || "—"}{section_name ? ` (${section_name})` : ""}</td>
                        <td className="small text-muted">
                          {Object.entries(rest)
                            .filter(([, v]) => v !== "" && v != null)
                            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
                            .join("  ·  ") || "—"}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-light" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── Saved batches list ─────────────────────────────────────────────
const SavedBatches = ({
  typeFilter,
  refreshSignal,
  onEdit,
}: {
  typeFilter?: string
  refreshSignal: number
  onEdit?: (batch: any) => void
}) => {
  const [batches, setBatches] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [genBatch, setGenBatch] = useState<any | null>(null)
  const [viewBatch, setViewBatch] = useState<any | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [picked, setPicked] = useState<Record<string, boolean>>({})
  const [merging, setMerging] = useState(false)

  const fetchBatches = async () => {
    setLoading(true)
    try {
      const res = await batchesApi.list(typeFilter ? { type: typeFilter } : {})
      if (res.success) setBatches(res.data)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load batches")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBatches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, refreshSignal])

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await batchesApi.remove(deleteId)
      toast.success("Batch deleted")
      setDeleteId(null)
      fetchBatches()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed")
    }
  }

  const pickedIds = Object.keys(picked).filter(k => picked[k])
  const mergeSelected = async () => {
    if (pickedIds.length < 2) return toast.error("Pick at least two batches to merge.")
    const types = new Set(batches.filter(b => picked[b._id]).map(b => b.type))
    if (types.size > 1) return toast.error("All selected batches must be the same type.")
    setMerging(true)
    try {
      const res = await batchesApi.merge({ batchIds: pickedIds })
      toast.success(`Merged ${pickedIds.length} batches → ${res.data?.refNumber || ""}`)
      setPicked({})
      fetchBatches()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Merge failed")
    } finally {
      setMerging(false)
    }
  }

  const q = search.trim().toLowerCase()
  const visible = !q
    ? batches
    : batches.filter(b => {
        const hay = [
          b.fileName,
          b.refNumber,
          typeLabel(b.type),
          ...(Array.isArray(b.studentsData)
            ? b.studentsData.map((r: any) => `${r.full_name || ""} ${r.gr_number || ""}`)
            : []),
        ]
          .join(" ")
          .toLowerCase()
        return hay.includes(q)
      })

  // RMS parity: source batches vs merge-output batches (their own section).
  const sources = visible.filter((b: any) => !(b.mergedFrom && b.mergedFrom.length))
  const mergedOutputs = visible.filter((b: any) => b.mergedFrom && b.mergedFrom.length)
  const byName = (b: any) =>
    b.createdBy ? `${b.createdBy.firstName || ""} ${b.createdBy.lastName || ""}`.trim() || "—" : "—"

  const columns = (
    <tr>
      <th style={{ width: 36 }} />
      <th>File Name</th>
      <th>Ref</th>
      <th>Session</th>
      <th>Campus</th>
      <th>Students</th>
      <th>Saved · Generated</th>
      <th>By</th>
      <th>Actions</th>
    </tr>
  )

  const renderRow = (b: any) => {
    const isMerged = !!b.mergedIntoBatch
    const isMergeOutput = b.mergedFrom && b.mergedFrom.length
    return (
      <tr key={b._id}>
        <td>
          <input type="checkbox" className="form-check-input" checked={!!picked[b._id]}
            disabled={isMerged || isMergeOutput}
            onChange={e => setPicked(prev => ({ ...prev, [b._id]: e.target.checked }))} />
        </td>
        <td>{b.fileName}</td>
        <td><span className="badge bg-secondary">{b.refNumber || "—"}</span></td>
        <td>{b.session?.title || b.session?.name || "—"}</td>
        <td>{b.branch?.name || <span className="text-muted">All campuses</span>}</td>
        <td>{Array.isArray(b.studentsData) ? b.studentsData.length : 0}</td>
        <td className="small">
          <div className="text-muted">{new Date(b.createdAt).toLocaleString()}</div>
          {b.lastGeneratedAt ? (
            <span className="badge bg-success-transparent text-success">✓ Generated {new Date(b.lastGeneratedAt).toLocaleDateString()}</span>
          ) : (
            <span className="text-muted">Not yet generated</span>
          )}
          {b.lastTemplateNames && <div className="text-muted">via {b.lastTemplateNames}</div>}
          {isMerged && b.mergedIntoBatch && (
            <div className="text-info"><i className="ti ti-link me-1" />Merged into {b.mergedIntoBatch.refNumber || b.mergedIntoBatch.fileName}</div>
          )}
        </td>
        <td className="small">{byName(b)}</td>
        <td>
          <div className="d-flex gap-1">
            <button className="btn btn-sm btn-primary" onClick={() => setGenBatch(b)}><i className="ti ti-certificate me-1" />Generate</button>
            {onEdit && !isMerged && (
              <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(b)} title="Edit roster"><i className="ti ti-edit" /></button>
            )}
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setViewBatch(b)}>View</button>
            <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(b._id)}><i className="ti ti-trash" /></button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div className="card mt-3">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 className="mb-0">
          Saved Batches{typeFilter ? ` — ${typeLabel(typeFilter)}` : ""} ({visible.length})
        </h5>
        <div className="d-flex align-items-center gap-2">
          {pickedIds.length >= 2 && (
            <button className="btn btn-sm btn-outline-success" onClick={mergeSelected} disabled={merging}>
              <i className="ti ti-arrows-join me-1" />
              {merging ? "Merging..." : `Merge ${pickedIds.length}`}
            </button>
          )}
          <input
            className="form-control form-control-sm"
            style={{ maxWidth: 280 }}
            placeholder="Search file, ref, student, GR..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="card-body table-responsive">
        {loading ? (
          <div className="text-center p-3"><span className="spinner-border text-primary" /></div>
        ) : visible.length === 0 ? (
          <div className="text-muted text-center p-3">No saved batches yet.</div>
        ) : (
          <>
            <table className="table table-bordered align-middle">
              <thead>{columns}</thead>
              <tbody>{sources.map(renderRow)}</tbody>
            </table>
            {mergedOutputs.length > 0 && (
              <>
                <h6 className="mt-3 mb-2 text-primary">
                  Merged batches ({mergedOutputs.length}){" "}
                  <small className="text-muted fw-normal">— combined outputs; each already includes the source batches' students (deduped).</small>
                </h6>
                <table className="table table-bordered align-middle">
                  <thead>{columns}</thead>
                  <tbody>{mergedOutputs.map(renderRow)}</tbody>
                </table>
              </>
            )}
          </>
        )}
      </div>

      {genBatch && (
        <GenerateModal batch={genBatch} onClose={() => setGenBatch(null)} onDone={fetchBatches} />
      )}
      {viewBatch && <ViewModal batch={viewBatch} onClose={() => setViewBatch(null)} />}

      {deleteId && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center p-4">
                  <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
                  <h4 className="mt-2">Delete Batch</h4>
                  <p className="text-muted">Delete this saved batch? Generated PDFs are not affected.</p>
                  <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  )
}

export default SavedBatches
